import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { clientsApi } from '@/services/api';
import { downloadCSV, readCSVFile, importCSV } from '@/utils/csv';
import { HiOutlinePlus, HiOutlineSearch, HiOutlineEye, HiOutlineDownload, HiOutlineUpload } from 'react-icons/hi';

interface Client {
  id: string;
  clientNumber: string;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  birthDate: string;
  gender: string;
  disabilityType: string;
  status: string;
  recipientNumber: string;
}

interface ClientsResponse {
  data: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

const statusLabels: Record<string, { label: string; class: string }> = {
  active: { label: '利用中', class: 'badge-success' },
  inactive: { label: '休止中', class: 'badge-warning' },
  waiting: { label: '待機中', class: 'badge-gray' },
  terminated: { label: '終了', class: 'badge-danger' },
};

const disabilityLabels: Record<string, string> = {
  physical: '身体',
  intellectual: '知的',
  mental: '精神',
  developmental: '発達',
  intractable: '難病',
  multiple: '重複',
};

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<ClientsResponse>({
    queryKey: ['clients', { page, search }],
    queryFn: () => clientsApi.list({ page, search }) as Promise<ClientsResponse>,
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadCSV('/api/v1/export/clients');
      toast.success('CSVをダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const csvText = await readCSVFile(file);
      const result = await importCSV('/api/v1/import/clients', csvText);
      
      if (result.errors?.length > 0) {
        toast.error(`${result.imported}件インポート、${result.errors.length}件エラー`);
        console.error('Import errors:', result.errors);
      } else {
        toast.success(`${result.imported}件インポートしました`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    } catch (error) {
      toast.error('インポートに失敗しました');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clients = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">利用者管理</h1>
          <p className="text-gray-600 mt-1">利用者の情報を管理します</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary"
          >
            <HiOutlineDownload className="w-5 h-5 mr-2" />
            {isExporting ? 'エクスポート中...' : 'CSVエクスポート'}
          </button>
          <button 
            onClick={handleImportClick}
            disabled={isImporting}
            className="btn-secondary"
          >
            <HiOutlineUpload className="w-5 h-5 mr-2" />
            {isImporting ? 'インポート中...' : 'CSVインポート'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
          <Link to="/clients/new" className="btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            新規登録
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="名前で検索..."
                className="input pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>利用者番号</th>
                <th>氏名</th>
                <th>生年月日</th>
                <th>障害種別</th>
                <th>受給者番号</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    利用者が見つかりません
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id}>
                    <td className="font-mono text-sm">{client.clientNumber}</td>
                    <td>
                      <div>
                        <p className="font-medium text-gray-900">
                          {client.lastName} {client.firstName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {client.lastNameKana} {client.firstNameKana}
                        </p>
                      </div>
                    </td>
                    <td>{client.birthDate}</td>
                    <td>{disabilityLabels[client.disabilityType] || client.disabilityType}</td>
                    <td className="font-mono text-sm">{client.recipientNumber}</td>
                    <td>
                      <span className={statusLabels[client.status]?.class || 'badge-gray'}>
                        {statusLabels[client.status]?.label || client.status}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/clients/${client.id}`}
                        className="btn-ghost text-sm py-1 px-2"
                      >
                        <HiOutlineEye className="w-4 h-4 mr-1" />
                        詳細
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > pagination.limit && (
          <div className="card-body border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {pagination.total}件中 {(pagination.page - 1) * pagination.limit + 1}-
                {Math.min(pagination.page * pagination.limit, pagination.total)}件を表示
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn-secondary text-sm"
                >
                  前へ
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * pagination.limit >= pagination.total}
                  className="btn-secondary text-sm"
                >
                  次へ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
